import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { parseKeyValueMap, parseRecordItems, parseSimpleList } from "@/lib/pageContent";
import { usePublicSettings } from "@/hooks/usePublicSiteData";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { getPublicUiCopy } from "@/lib/publicPageLocalization";
import {
  ArrowLeft,
  Award,
  BriefcaseBusiness,
  Building2,
  GraduationCap,
  Languages,
  MapPin,
  Quote,
  Star,
} from "lucide-react";

export default function CEOProfile() {
  const { language } = useSiteLanguage();
  const { data: settings } = usePublicSettings();
  const ui = getPublicUiCopy(language, "ceoProfile");

  const ceoName = settings?.proprietor || "Engr. Dibakar Howlader";
  const ceoTitle = settings?.ceo_title || "Current Owner & Chief Executive Officer";
  const founderName = settings?.founder_name || "Shankar Kumar Howlader";
  const companySince = settings?.company_since || "1985";
  const companyLocation = settings?.company_location || "Barishal, Bangladesh";
  const ceoPhoto = settings?.ceo_photo_url || "/ceo-dibakar.png";
  const ceoIntro =
    settings?.ceo_intro ||
    "R.S. International was founded in 1985 by his father, Shankar Kumar Howlader. Engr. Dibakar Howlader, the only son of Shankar Kumar Howlader, is the current owner and Chief Executive Officer, leading the company with a strong background in engineering, entrepreneurship, infrastructure, and cross-border leadership. He combines technical discipline, field experience, and a global outlook to guide the business with long-term trust, structure, and impact.";
  const ceoQuote =
    settings?.ceo_quote ||
    "Our success is measured by the trust we build, the quality we deliver, and the long-term trade relationships we create across borders.";
  const highlights = parseSimpleList(
    settings?.ceo_profile_highlights || "Civil Engineer\nAward-winning entrepreneur\nCommunity-focused business leader"
  );
  const visionParagraphs = parseSimpleList(settings?.ceo_profile_vision_paragraphs || "");
  const visionPoints = parseSimpleList(settings?.ceo_profile_vision_points || "");
  const leadershipParagraphs = parseSimpleList(settings?.ceo_profile_leadership_paragraphs || "");
  const milestones = parseSimpleList(settings?.ceo_profile_milestones || "");
  const experience = parseRecordItems(settings?.ceo_profile_experience_items || "", ["period", "location", "company", "role", "points"]).map((item) => ({
    ...item,
    points: item.points.split("~~").map((point) => point.trim()).filter(Boolean),
  }));
  const languages = parseRecordItems(settings?.ceo_profile_languages || "", ["name", "level"]);
  const education = parseRecordItems(settings?.ceo_profile_education_items || "", ["title", "subtitle", "school", "extra", "body"]);
  const awards = parseRecordItems(settings?.ceo_profile_awards_items || "", ["date", "title", "issuer", "note"]);
  const commonLabels = parseKeyValueMap(settings?.profile_common_labels || "");
  const sectionLabels = parseKeyValueMap(settings?.ceo_profile_section_labels || "");

  return (
    <>
      <section className="bg-[var(--rsi-bg-subtle)] py-20 lg:py-24" data-testid="ceo-profile-hero">
        <div className="container-rsi grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <Link to="/about" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--rsi-purple)]">
              <ArrowLeft className="h-4 w-4" /> {commonLabels.back || "Back to About"}
            </Link>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--rsi-purple-tint)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--rsi-purple)]">
              <BriefcaseBusiness className="h-3.5 w-3.5" /> {settings?.ceo_profile_badge || "CEO Profile"}
            </div>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl font-black tracking-tighter text-[var(--rsi-navy)]">
              {ceoName}
            </h1>
            <p className="mt-3 text-lg font-medium text-[var(--rsi-purple)]">
              {ceoTitle}
            </p>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-700">
              {ceoIntro}
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[ceoTitle, ...highlights].map((item) => (
                <div key={item} className="rounded-2xl border border-white bg-white px-4 py-4 shadow-sm text-sm font-semibold text-[var(--rsi-navy)]">
                  {item}
                </div>
              ))}
            </div>
          </motion.div>

          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-[28px] border border-[var(--rsi-purple)]/10 bg-white shadow-2xl shadow-[var(--rsi-purple)]/10">
              <div className="h-24 bg-gradient-to-r from-[var(--rsi-purple)] to-[var(--rsi-navy)]" />
              <div className="px-6 pb-6 -mt-12">
                <div className="overflow-hidden rounded-3xl border-4 border-white shadow-xl">
                  <img
                    src={ceoPhoto}
                    alt={ceoName}
                    className="h-[420px] w-full object-cover object-top"
                  />
                </div>
                <div className="mt-5 grid gap-3 text-sm text-gray-600">
                  <div className="inline-flex items-center gap-2 rounded-full bg-[var(--rsi-bg-subtle)] px-3 py-2 w-fit">
                    <MapPin className="h-4 w-4 text-[var(--rsi-purple)]" /> {companyLocation}
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-[var(--rsi-bg-subtle)] px-3 py-2 w-fit">
                    <Building2 className="h-4 w-4 text-[var(--rsi-purple)]" /> {ui.foundedIn} {companySince} {ui.foundedBy} {founderName}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="ceo-profile-content">
        <div className="container-rsi grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-8">
            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <Award className="h-4 w-4" /> {sectionLabels.vision || "Professional Vision"}
              </div>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
                {settings?.ceo_profile_vision_title || "Structured leadership with a global mindset"}
              </h2>
              <div className="mt-5 space-y-4 text-gray-700 leading-relaxed">
                {visionParagraphs.map((item) => <p key={item}>{item}</p>)}
                {visionPoints.map((point) => (
                  <div key={point} className="rounded-2xl bg-[var(--rsi-bg-subtle)] px-5 py-4">
                    {point}
                  </div>
                ))}
              </div>
            </div>

            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <BriefcaseBusiness className="h-4 w-4" /> {sectionLabels.leadership || "Entrepreneurial Leadership"}
              </div>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
                {settings?.ceo_profile_leadership_title || "A leader shaped by execution, trust, and ambition"}
              </h2>
              <div className="mt-5 space-y-4 text-gray-700 leading-relaxed">
                {leadershipParagraphs.map((item) => <p key={item}>{item}</p>)}
                {milestones.map((item) => (
                  <div key={item} className="rounded-2xl border border-gray-100 px-5 py-4">
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <Building2 className="h-4 w-4" /> {sectionLabels.experience || "Professional Experience"}
              </div>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
                {ui.experienceTitle}
              </h2>
              <div className="mt-6 space-y-6">
                {experience.map((item) => (
                  <div key={`${item.company}-${item.period}`} className="rounded-3xl border border-gray-100 p-6">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="font-display text-xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
                          {item.company}
                        </div>
                        <div className="mt-1 text-sm font-semibold text-[var(--rsi-purple)]">{item.role}</div>
                      </div>
                      <div className="text-sm text-gray-500">
                        <div>{item.period}</div>
                        <div>{item.location}</div>
                      </div>
                    </div>
                    <div className="mt-4 space-y-3 text-sm leading-relaxed text-gray-700">
                      {item.points.map((point) => (
                        <div key={point} className="flex gap-3">
                          <span className="mt-2 h-2 w-2 rounded-full bg-[var(--rsi-purple)] flex-shrink-0" />
                          <span>{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <Quote className="h-4 w-4" /> {sectionLabels.philosophy || "Leadership Philosophy"}
              </div>
              <blockquote className="mt-4 font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)] leading-tight">
                "{ceoQuote}"
              </blockquote>
              <p className="mt-4 text-gray-600 text-sm">
                {settings?.ceo_profile_philosophy_note ||
                  "This reflects the company's export-import philosophy: dependable sourcing, transparent dealing, and lasting value for global partners."}
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-8">
            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <Languages className="h-4 w-4" /> {sectionLabels.languages || "Global Competency"}
              </div>
              <div className="mt-5 space-y-3">
                {languages.map((item) => (
                  <div key={item.name} className="flex items-center justify-between rounded-2xl bg-[var(--rsi-bg-subtle)] px-4 py-3">
                    <span className="font-semibold text-[var(--rsi-navy)]">{item.name}</span>
                    <span className="text-sm text-gray-600">{item.level}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <GraduationCap className="h-4 w-4" /> {sectionLabels.education || "Academic Credentials"}
              </div>
              <div className="mt-5 space-y-4">
                {education.map((item) => (
                  <div key={item.title} className="rounded-2xl border border-gray-100 px-5 py-4">
                    <div className="font-semibold text-[var(--rsi-navy)]">{item.title}</div>
                    <div className="mt-1 text-sm text-gray-700">{item.subtitle}</div>
                    <div className="mt-1 text-sm text-gray-600">{item.school}</div>
                    {item.extra && <div className="mt-2 text-xs text-[var(--rsi-purple)] font-semibold">{item.extra}</div>}
                    {item.body && <div className="mt-3 text-sm leading-relaxed text-gray-600">{item.body}</div>}
                  </div>
                ))}
              </div>
            </div>

            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <Star className="h-4 w-4" /> {sectionLabels.awards || "Awards"}
              </div>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
                {settings?.ceo_profile_awards_title || "Recognition that reflects public confidence"}
              </h2>
              <p className="mt-4 text-gray-700 leading-relaxed">
                {settings?.ceo_profile_awards_intro ||
                  "These recognitions strengthen the credibility of his profile and reflect how his work has been valued by respected business and entrepreneur platforms in the region. They present him not only as a capable executive, but as a leader whose efforts have made a visible impression on others."}
              </p>
              <div className="mt-5 space-y-4">
                {awards.map((item) => (
                  <div key={`${item.date}-${item.title}`} className="rounded-2xl border border-gray-100 px-5 py-4">
                    <div className="text-xs uppercase tracking-[0.18em] text-gray-400">{item.date}</div>
                    <div className="mt-1 font-semibold text-[var(--rsi-navy)]">{item.title}</div>
                    <div className="mt-1 text-sm text-[var(--rsi-purple)]">{item.issuer}</div>
                    <div className="mt-2 text-sm text-gray-600">{item.note}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
